import { Lastgang } from './Lastgang';
import * as Moment from 'moment';
import * as XLSX from 'xlsx';

export class LastgangProcess extends Lastgang {

    constructor(data: any, config: any) {
        super(data, config);
        this.initKeys();
    }

    protected energyUnits: { [key: string]: string } = { wh: 'Wh', kwh: 'kWh', mwh: 'mWh' };
    protected interval: number = null;
    protected intervals: any = {};
    protected dataKeys: any[] = [];
    protected powerUnits: { [key: string]: string } = { w: 'W', kw: 'kW', mw: 'mW' };

    protected getAggregates(data: any[], keys: string[]): any {
        const len: number = (data || []).length;
        const indexMax: any = {};
        const indexMin: any = {};
        const max: any = {};
        const min: any = {};
        const sum: any = {};
        const values: any = {};

        keys.forEach((key: string) => {
            const hasValues: boolean = typeof ((data || [])[0] || {})[key] === 'number';
            indexMax[key] = hasValues ? 0 : null;
            indexMin[key] = hasValues ? 0 : null;
            max[key] = hasValues ? data[0][key] : null;
            min[key] = hasValues ? data[0][key] : null;
            sum[key] = hasValues ? data[0][key] : null;

            for (let i: number = 1; i < len; ++i) {
                let value: number = data[i][key];

                if (typeof value !== 'number') {
                    continue;
                }

                if (value > max[key]) {
                    indexMax[key] = i;
                    max[key] = value;
                }

                if (value < min[key]) {
                    indexMin[key] = i;
                    min[key] = value;
                }
                sum[key] += value;
            }
            values[key] = {
                avg: sum[key] !== null ? sum[key] / len : null,
                max: data[indexMax[key]],
                min: data[indexMin[key]],
                sum: sum[key]
            };
        });

        return values;
    }
    /* overrides parent method */
    public getColumns(): any[] {
        return (this.dataKeys || []).map((item: any) => ({
            ...(item.column || {}), isEnergy: item.isEnergy || false, isPower: item.isPower || false
        }));
    }

    public getDateMax(): any {
        const data: any[] = this.data || [];

        return (data[data.length - 1] || {}).datetime || null;
    }

    public getDateMin(): any {
        const data: any[] = this.data || [];

        return (data[0] || {}).datetime || null;
    }

    protected getEnergy(value: number): number {
        if (typeof value !== 'number' || +this.interval <= 0) {
            return null;
        }

        return value * (this.interval / 3600);
    }

    protected getEnergyIndex(): number {
        const energyKeys: string[] = Object.keys(this.energyUnits || {});

        return (super.getUnits() || []).findIndex((unit: any) => energyKeys.indexOf((unit || {}).key) !== -1);
    }

    public getEnergyUnits(): any {
        return this.energyUnits || {};
    }

    public getExportFile(data: any[], opts?: any): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data || []);
        const workbook: XLSX.WorkBook = XLSX.utils.book_new();

        if (Array.isArray((opts || {}).columnWidths)) {
            worksheet['!cols'] = opts.columnWidths.map((wch: number) => ({ wch }));
        }
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Lastgang');
        XLSX.writeFile(workbook, `Lastgang [${Moment().format('DD.MM.YYYY')}].xlsx`);
    }

    public getInterval(): number {
        return this.interval;
    }

    public getIntervalData(filter: any): any {
        const chunks: any = {};
        const data: any = { all: this.data || [] };
        const energyKeys: string[] = Object.keys(this.energyUnits || {});
        const indexEnergy: number = this.getEnergyIndex();
        const indexPower: number = this.getPowerIndex();
        const intervals: string[] = Object.keys(this.intervals);
        const len: number = data.all.length - 1;
        const powerKeys: string[] = Object.keys(this.powerUnits || {});
        const units: any[] = this.getUnitKeys();
        const weekdays: number[] = Array.isArray((filter || {}).weekdays) ? filter.weekdays : [];
        let skip: boolean = false;
        this.setInterval(data.all);
        intervals.forEach((interval: string) => {
            chunks[interval] = [];
            data[interval] = [];
        });

        data.all.forEach((row: any, n: number) => {
            const isLast: boolean = len === n;

            if ((weekdays.length || isLast) && weekdays.indexOf(Moment.unix(+row.datetime / 1000).isoWeekday()) === -1) {
                if (isLast) {
                    skip = true;
                } else {
                    return;
                }
            }
            const keys: string[] = [];

            (row.values || []).forEach((value: any, index: number) => {
                keys.push(`value${index}`);
                row[`value${index}`] = typeof value === 'number' ? value : null;

                if (index === indexEnergy && indexPower === -1) {
                    const suffix: string = powerKeys[energyKeys.indexOf(units[index] || null)] || '';
                    keys.push(`value${index}-${suffix}`);
                    row[`value${index}-${suffix}`] = this.getPower(value);
                } else if (index === indexPower && indexEnergy === -1) {
                    const suffix: string = energyKeys[powerKeys.indexOf(units[index] || null)] || '';
                    keys.push(`value${index}-${suffix}`);
                    row[`value${index}-${suffix}`] = this.getEnergy(value);
                }
            });
            row.stamp = +row.datetime;
            const stamp = row.stamp / 1000;

            intervals.forEach((interval: string) => {
                const isDifferent: boolean = this.isDifferentInterval(
                    [stamp, +(chunks[interval][chunks[interval].length - 1] || {}).stamp / 1000], interval
                );

                if (isDifferent || isLast) {
                    const process = (): void => {
                        const list: any[] = [];

                        if (interval === '1#h' && this.isDstShift(chunks[interval])) {
                            list.push(...this.splitByStamp(chunks[interval]) || []);
                        } else {
                            list.push(chunks[interval]);
                        }

                        list.forEach((chunk: any) => {
                            const aggregates: any = this.getAggregates(chunk, keys);
                            const values: any = {};

                            keys.forEach((key: string, index: number) => {
                                const unit: string = units[index] || null;
                                const max: any = (aggregates[key] || {}).max || {};
                                const min: any = (aggregates[key] || {}).min || {};
                                values[key] = energyKeys.indexOf(unit) !== -1
                                    ? (typeof (aggregates[key] || {}).sum === 'number' ? +aggregates[key].sum.toFixed(3) : null)
                                    : (typeof (aggregates[key] || {}).avg === 'number' ? +aggregates[key].avg.toFixed(3) : null);
                                values[`${key}-max`] = max[key];
                                values[`${key}-max-stamp`] = max.stamp;
                                values[`${key}-min`] = min[key];
                                values[`${key}-min-stamp`] = min.stamp;
                            });
                            data[interval].push({
                                stamp: (chunk[0] || {}).stamp || row.stamp,
                                datetime: (chunk[0] || {}).datetime || row.datetime,
                                ...values
                            });
                        });
                    };

                    if (!isDifferent && isLast && !skip) {
                        chunks[interval].push(row);
                    }
                    process();
                    chunks[interval] = skip ? [] : [row];

                    if (isDifferent && isLast && chunks[interval].length) {
                        process();
                    }
                } else {
                    chunks[interval].push(row);
                }
            });
        });

        return data;
    }

    protected getPower(value: number): number {
        if (typeof value !== 'number' || +this.interval <= 0) {
            return null;
        }

        return value / (this.interval / 3600);
    }

    protected getPowerIndex(): number {
        const powerKeys: string[] = Object.keys(this.powerUnits || {});

        return (super.getUnits() || []).findIndex((unit: any) => powerKeys.indexOf((unit || {}).key) !== -1);
    }

    public getPowerUnits(): any {
        return this.powerUnits || {};
    }

    protected getUnitKeys(): string[] {
        const data: any[] = [];
        const energyKeys: string[] = Object.keys(this.energyUnits || {});
        const indexEnergy: number = this.getEnergyIndex();
        const indexPower: number = this.getPowerIndex();
        const powerKeys: string[] = Object.keys(this.powerUnits || {});
        const units: any[] = super.getUnits() || [];
        let energy: boolean = false;
        let power: boolean = false;

        (((this.data || [])[0] || {}).values || []).forEach((value: number, index: number) => {
            const unit: string = (units[index] || {}).key || null;

            if (unit) {
                data.push(unit);

                if (powerKeys.indexOf(unit) !== -1 && indexEnergy === -1 && !energy) {
                    data.push(`${unit}h`)
                    energy = true;
                } else if (energyKeys.indexOf(unit) !== -1 && indexPower === -1 && !power) {
                    data.push(unit.substr(0, unit.length - 1));
                    power = true;
                }
            } else {
                data.push(null);
            }
        });

        return data;
    }
    /* overrides parent method */
    public getUnits(): any[] {
        return (this.dataKeys || []).map((item: any) => ({
            ...(item.unit || {}), isEnergy: item.isEnergy || false, isPower: item.isPower || false
        }));
    }

    protected getWeekdaysByStamp(stamp: number): any[] {
        if (!Moment.unix(stamp).isValid()) {
            return [];
        }
        const data: any[] = [];

        for (let i: number = 1; i <= 7; ++i) {
            data.push(Moment.unix(stamp).isoWeekday(i).hour(0).minute(0).second(0));
        }

        return data;
    }

    protected getWeekdaysRangeByStamp(stamp: number): number[] {
        if (!Moment.unix(stamp).isValid()) {
            return [];
        }

        return [
            +Moment.unix(stamp).isoWeekday(1).hour(0).minute(0).second(0) / 1000,
            +Moment.unix(stamp).isoWeekday(7).hour(23).minute(59).second(59) / 1000
        ];
    }

    protected initKeys(): void {
        const energyKeys: string[] = Object.keys(this.energyUnits || {});
        const powerKeys: string[] = Object.keys(this.powerUnits || {});
        const indexEnergy: number = this.getEnergyIndex();
        const indexPower: number = this.getPowerIndex();
        const units: any[] = this.getUnitKeys();
        this.dataKeys = [];

        (((this.data || [])[0] || {}).values || []).forEach((value: any, index: number) => {
            this.dataKeys.push({ column: { ...this.dataColumns[index], key: `value${index}` }, unit: this.dataUnits[index] });

            if (index === indexEnergy) {
                if (indexPower === -1) {
                    const key: string = powerKeys[energyKeys.indexOf(units[index] || null)] || '';
                    this.dataKeys.push({
                        column: { name: 'Leistung', key: `value${index}-${key}` }, isPower: true,
                        unit: { name: (this.powerUnits || {})[key] || null, key }
                    });
                }
                this.dataKeys[index].isEnergy = true;
            } else if (index === indexPower) {
                if (indexEnergy === -1) {
                    const key: string = energyKeys[powerKeys.indexOf(units[index] || null)] || '';
                    this.dataKeys.push({
                        column: { name: 'Verbrauch', key: `value${index}-${key}` }, isEnergy: true,
                        unit: { name: (this.energyUnits || {})[key] || null, key }
                    });
                }
                this.dataKeys[index].isPower = true;
            }
        });
    }

    protected isDifferentInterval(stamps: number[], interval: string): boolean {
        const getStart = (value: number): number => Math.floor(value / size) * size;
        const stamp: number = stamps[0] || null;
        const stamp2: number = stamps[1] || null;
        let max: number = null;
        let min: number = null;
        let size: number = +interval.split('#')[0] || 1;

        switch (interval.split('#')[1]) {
            case 'd':
                max = +Moment.unix(stamp).hour(0).minute(0).second(0).add({ day: 1, second: -1 }) / 1000;
                min = +Moment.unix(stamp).hour(0).minute(0).second(0) / 1000;
                break;
            case 'h': {
                const start: number = getStart(Moment.unix(stamp).hour());
                max = +Moment.unix(stamp).hour(start).minute(0).second(0).add({ hour: size, second: -1 }) / 1000;
                min = +Moment.unix(stamp).hour(start).minute(0).second(0) / 1000;

                if (Moment.unix(stamp).utcOffset() < Moment.unix(min).utcOffset()) {
                    max = +Moment.unix(stamp).hour(start).minute(0).second(0).add({ hour: size + 1, second: -1 }) / 1000;
                }
                break;
            }
            case 'Q':
                size *= 3;
                // no break
            case 'M': {
                const start: number = getStart(Moment.unix(stamp).month());
                max = +Moment.unix(stamp).month(start).date(1).hour(0).minute(0).second(0).add({ month: size, second: -1 }) / 1000;
                min = +Moment.unix(stamp).month(start).date(1).hour(0).minute(0).second(0) / 1000;
                break;
            }
            case 'w':
                const weekdays: number[] = this.getWeekdaysRangeByStamp(stamp) || [];

                if (weekdays.length === 2) {
                    max = +weekdays[1] || null;
                    min = +weekdays[0] || null;
                }
                break;
            case 'y':
                max = +Moment.unix(stamp).month(0).date(1).hour(0).minute(0).second(0).add({ year: 1, second: -1 }) / 1000;
                min = +Moment.unix(stamp).month(0).date(1).hour(0).minute(0).second(0) / 1000;
                break;
            default:
                return true;
        }

        return !(stamp2 === null || (stamp2 >= min && stamp2 <= max));
    }

    protected isDstShift(data: any[]): boolean {
        return Array.from(new Set((data || []).map((item: any) => item.stamp))).length !== data.length;
    }

    protected setInterval(data: any[]): void {
        if (
            (data || []).length < 2 || typeof +data[0].datetime !== 'number' || typeof +data[1].datetime !== 'number'
            || (+data[0].datetime >= +data[1].datetime)
        ) {
            return;
        }
        this.interval = (+data[1].datetime - +data[0].datetime) / 1000;
    }

    public setIntervals(data: any): void {
        this.intervals = data || {};
    }

    protected splitByStamp(data: any[]): any[] {
        const chunks: any[] = [[]];
        const stamps: number[] = [];

        data.forEach((item: any) => {
            if (typeof item.stamp === 'number' && stamps.indexOf(item.stamp) === -1) {
                chunks[0].push(item);
                stamps.push(item.stamp);
            } else {
                if (!chunks[1]) {
                    chunks[1] = [];
                }
                chunks[1].push(item);
            }
        });

        return chunks;
    }
}
